import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Box, Button, FormField, FormGrid, Type } from "mesh-component-library";
import { YesNoButtonGroup } from "./YesNoButtonGroup";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { useHistory } from "react-router-dom";
import { generatePath } from "../../utilities/RedirectUtils";
import { datadogRum } from "@datadog/browser-rum";
import Spinner from "../../components/spinner";
import { useSelector } from "react-redux";
import { getIncidentDetails, getIsServiceTow } from "../../selector";
import _isEmpty from "lodash/isEmpty";

const Container = styled.div`
  padding: 16px;
  gap: 16px;
  display: flex;
  flex-direction: column;
`;

type IncidentReportQuestion = {
  orderNumber: number;
  questionId: number;
  questionText: string;
  responseType: string;
  responseText?: string;
};

export type IncidentReportFormProps = {
  customerType: string;
  onCompleteForm: (data: Record<string, any>) => void;
  onInit: () => void;
  prefilledData: Record<string, { value: string }>;
  questions: IncidentReportQuestion[];
};

export const IncidentReportForm = ({
  customerType,
  onCompleteForm,
  onInit,
  prefilledData,
  questions,
}: IncidentReportFormProps) => {
  const history = useHistory();
  const isUberDriver = customerType === "uber";
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [formState, setFormState] = useState({});
  const getTranslatedText = useLocale();
  const incidentDetails = useSelector(getIncidentDetails);
  const isTow = useSelector(getIsServiceTow);
  const effectiveQuestions = !_isEmpty(incidentDetails) ? incidentDetails : questions;

  /** Fetch questions via API */
  useEffect(() => {
    if (_isEmpty(incidentDetails)) {
      onInit && onInit();
    }
  }, []);

  /** Initialize form state */
  useEffect(() => {
    setFormState(initializeFormState());
  }, [effectiveQuestions]);

  /** Validate form is filled out correctly */
  useEffect(() => {
    if (validateFormIsComplete()) {
      setDisableSubmit(false);
    } else {
      setDisableSubmit(true);
    }
  }, [formState]);

  const handleSubmit = (data) => {
    onCompleteForm(data);
    history.push(generatePath("location"));
    datadogRum.addAction("Incident Report Form Submitted", data);
  };

  const initializeFormState = () =>
    effectiveQuestions.reduce(
      (acc, { questionId, responseText, questionText }) => ({
        ...acc,
        [questionId]:
          isUberQuestion(questionText) && !isUberDriver ? "N/A" : responseText || prefilledData[questionId]?.value,
      }),
      {}
    );

  const onInputChange = (value, questionId: number) => {
    const maxLength = 150;
    if (value?.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    setFormState((prevState) => ({ ...prevState, [questionId]: value }));
  };

  const orderArrayOfQuestions = (questionArray: IncidentReportQuestion[]) =>
    [...questionArray].sort((a, b) => a.orderNumber - b.orderNumber);

  /**
   * There are some questions that require specific UI elements
   * The only way to key off is the question id by the backend, so these are hardcoded and must be maintained manually
   */
  const renderQuestions = (questions: IncidentReportQuestion[]) =>
    orderArrayOfQuestions(questions).map(({ questionId, questionText, responseType }) => {
      if (questionId === 751 || questionId === 4732) {
        /** This question requires a larger text area */
        return renderFormField({
          questionId,
          questionText,
          displayTextArea: true,
        });
      } else if (!isTow && questionId === 4683) {
        /** This question response is defaulted to "One", and disabled */
        return wrapQuestionInGridColumn(
          <FormField
            value="One"
            label={questionText}
            required={true}
            key={`incidentReportFormQuestion-${questionId}`}
            disabled
          />,
          `incidentReportFormColumn-${questionId}`
        );
      } else if (!isTow && questionId === 814) {
        return renderYesNoButtonGroup({
          questionId,
          questionText,
          disabled: true,
        });
      } else if (isUberQuestion(questionText)) {
        /** Only display these questions if the customer is uberDrive, and trim the content */
        return isUberDriver
          ? responseType === "radio_button"
            ? renderYesNoButtonGroup({
                questionId,
                questionText,
                isUber: true,
              })
            : renderFormField({ questionId, questionText, isUber: true })
          : null;
      }
      return responseType === "radio_button"
        ? renderYesNoButtonGroup({ questionId, questionText })
        : renderFormField({ questionId, questionText });
    });

  const renderYesNoButtonGroup = ({ questionId, questionText, isUber = false, disabled = false }) =>
    wrapQuestionInGridColumn(
      <YesNoButtonGroup
        onChange={(e) => onInputChange(e.target.value, questionId)}
        label={isUber ? trimUberQuestion(questionText) : questionText}
        value={formState[questionId]}
        key={`incidentReportFormQuestion-${questionId}`}
        name={`incidentReportFormQuestion-${questionId}`}
        disabled={disabled}
      />,
      `incidentReportFormColumn-${questionId}`
    );

  const renderFormField = ({ questionId, questionText, displayTextArea = false, isUber = false }) =>
    wrapQuestionInGridColumn(
      <FormField
        label={isUber ? trimUberQuestion(questionText) : questionText}
        value={formState[questionId]}
        onChange={(e) => onInputChange(e.target.value, questionId)}
        required={true}
        key={`incidentReportFormQuestion-${questionId}`}
        maxLength={150}
        type={displayTextArea ? "textarea" : "text"}
        rows={displayTextArea ? 5 : null}
        showCharCount
      />,
      `incidentReportFormColumn-${questionId}`
    );

  const trimUberQuestion = (questionText) => questionText.replace("When Uber Driver:", "");

  const isUberQuestion = (questionText) => questionText.includes("When Uber Driver:");

  const validateFormIsComplete = () => {
    return effectiveQuestions.every(({ questionId, responseType }) => {
      let fieldValue = formState[questionId];
      if (!fieldValue) {
        return false;
      } else {
        const isYesOrNo = responseType === "radio_button";
        if (isYesOrNo) {
          return fieldValue?.length >= 1;
        } else {
          return fieldValue?.length >= 2;
        }
      }
    });
  };

  const wrapQuestionInGridColumn = (questionComponent: React.ReactNode, key: string) => (
    <FormGrid.Column key={key}>{questionComponent}</FormGrid.Column>
  );

  return effectiveQuestions.length > 0 ? (
    <Container>
      <Type size="3xl" align="left" weight="bold">
        {getTranslatedText(TRANSLATION_CONSTANTS.TELL_US_MORE_ABOUT_THIS_INCIDENT)}
      </Type>
      <Box style={{ textAlign: "left" }}>
        <FormGrid>{renderQuestions(effectiveQuestions)}</FormGrid>
      </Box>
      <Button
        hasUpgrade
        type="submit"
        onClick={() => handleSubmit(formState)}
        utils={{ fullWidth: true }}
        disabled={disableSubmit}
      >
        Complete
      </Button>
    </Container>
  ) : (
    <Spinner />
  );
};
